const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  getToken() {
    return this.token || localStorage.getItem('admin_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('admin_token');
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const config = {
      ...options,
      headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, config);

    if (!response.ok) {
      // Try JSON first, then text, then fall back to a status-aware message
      let detail = '';
      try {
        const data = await response.clone().json();
        detail = data?.detail || data?.message || '';
      } catch (_) {
        try {
          const text = await response.text();
          // If the body is plain text (e.g. a proxy error), surface it
          if (text && text.length < 200) detail = text;
        } catch (_) { /* ignore */ }
      }
      if (!detail) {
        if (response.status === 401) detail = 'Invalid credentials';
        else if (response.status === 403) detail = 'Not authorized';
        else if (response.status === 429) detail = 'Too many attempts — try again in a few minutes';
        else if (response.status >= 500) detail = `Server error (${response.status}). Please try again.`;
        else detail = `Request failed (${response.status})`;
      }
      throw new Error(detail);
    }

    return response.json();
  }

  // Auth
  async login(email, password) {
    // Returns either { token, email } (no 2FA) or { requires_2fa: true, challenge_token }.
    const data = await this.request('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipAuth: true,
    });
    if (data && data.token && !data.requires_2fa) {
      this.setToken(data.token);
    }
    return data;
  }

  async login2fa(challenge_token, code) {
    const data = await this.request('/api/admin/login/2fa', {
      method: 'POST',
      body: JSON.stringify({ challenge_token, code }),
      skipAuth: true,
    });
    if (data && data.token) this.setToken(data.token);
    return data;
  }

  async verifyToken() {
    return this.request('/api/admin/verify');
  }

  // Upload
  async uploadImage(file, folder = 'images') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch(`${API_URL}/api/upload/image?folder=${folder}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }

  async uploadVideo(file, onProgress = null) {
    // User explicitly requested direct S3 upload for large files (1GB+)
    return this.uploadVideoDirectToS3(file, onProgress);
  }

  async uploadVideoDirectToS3(file, onProgress = null) {
    try {
      console.log('Starting direct S3 upload for:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      // Step 1: Get presigned upload URL from our backend
      // We explicitly send the file type so the backend generates a matching signature
      const presignedResponse = await fetch(`${API_URL}/api/upload/get-video-upload-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'video/mp4', // Fallback, but modern browsers usually have this
          file_size: file.size,
        }),
      });

      if (!presignedResponse.ok) {
        const error = await presignedResponse.json();
        throw new Error(error.detail || 'Failed to get upload URL');
      }

      const { upload_url, cdn_url, content_type } = await presignedResponse.json();
      console.log('Got upload URL. Required Content-Type:', content_type);

      // Step 2: Upload directly to S3 using presigned PUT URL
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        if (onProgress) {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              onProgress(percentComplete, event.loaded, event.total);
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('Upload complete!');
            resolve({
              url: cdn_url,
              filename: file.name,
              size_mb: Math.round(file.size / (1024 * 1024) * 10) / 10
            });
          } else {
            console.error('S3 upload failed:', xhr.status, xhr.responseText);
            // CORS errors often appear as status 0 or 403
            if (xhr.status === 0) {
               reject(new Error('Network Error: Likely a CORS issue on the S3 bucket. Please check AWS S3 CORS configuration.'));
            } else {
               reject(new Error(`Upload failed with status ${xhr.status}. Check console for details.`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          console.error('XHR Network Error');
          reject(new Error('Network error during upload. This is often caused by S3 CORS blocking the request.'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        // Upload directly to S3 using PUT
        xhr.open('PUT', upload_url);
        
        // CRITICAL: Content-Type must match EXACTLY what was used to sign the URL
        xhr.setRequestHeader('Content-Type', content_type);
        
        // Send the raw file
        xhr.send(file);
      });
    } catch (error) {
      console.error('Direct S3 upload error:', error);
      throw error;
    }
  }

  async uploadImageWithProgress(file, folder = 'images', onProgress = null) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete, event.loaded, event.total);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid response'));
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

      xhr.open('POST', `${API_URL}/api/upload/image?folder=${folder}`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      xhr.send(formData);
    });
  }

  async uploadMultipleImages(files, folder = 'images') {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('folder', folder);

    const response = await fetch(`${API_URL}/api/upload/multiple-images?folder=${folder}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }

  async deleteFile(fileUrl) {
    return this.request('/api/upload/file', {
      method: 'DELETE',
      body: JSON.stringify({ file_url: fileUrl }),
    });
  }

  // Hero
  async getHero() {
    return this.request('/api/content/hero', { skipAuth: true });
  }

  async updateHero(data) {
    return this.request('/api/content/hero', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Collections
  async getCollections() {
    return this.request('/api/content/collections', { skipAuth: true });
  }

  async createCollection(data) {
    return this.request('/api/content/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCollection(id, data) {
    return this.request(`/api/content/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCollection(id) {
    return this.request(`/api/content/collections/${id}`, {
      method: 'DELETE',
    });
  }

  // Films
  async getFilms() {
    return this.request('/api/content/films', { skipAuth: true });
  }

  async createFilm(data) {
    return this.request('/api/content/films', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFilm(id, data) {
    return this.request(`/api/content/films/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFilm(id) {
    return this.request(`/api/content/films/${id}`, {
      method: 'DELETE',
    });
  }

  // Testimonials
  async getTestimonials() {
    return this.request('/api/content/testimonials', { skipAuth: true });
  }

  async createTestimonial(data) {
    return this.request('/api/content/testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTestimonial(id, data) {
    return this.request(`/api/content/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTestimonial(id) {
    return this.request(`/api/content/testimonials/${id}`, {
      method: 'DELETE',
    });
  }

  // FAQs
  async getFAQs() {
    return this.request('/api/content/faqs', { skipAuth: true });
  }

  async createFAQ(data) {
    return this.request('/api/content/faqs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFAQ(id, data) {
    return this.request(`/api/content/faqs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFAQ(id) {
    return this.request(`/api/content/faqs/${id}`, {
      method: 'DELETE',
    });
  }

  // Submissions
  async getSubmissions() {
    return this.request('/api/content/submissions');
  }

  async updateSubmissionStatus(id, status) {
    return this.request(`/api/content/submissions/${id}/status?status=${status}`, {
      method: 'PATCH',
    });
  }

  async deleteSubmission(id) {
    return this.request(`/api/content/submissions/${id}`, {
      method: 'DELETE',
    });
  }
  // About & Inquire Content
  async getAboutContent() {
    return this.request('/api/content/about-content', { skipAuth: true });
  }

  async updateAboutContent(data) {
    return this.request('/api/content/about-content', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getInquireContent() {
    return this.request('/api/content/inquire-content', { skipAuth: true });
  }

  async updateInquireContent(data) {
    return this.request('/api/content/inquire-content', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Photo Page Content
  async getPhotoPageContent() {
    return this.request('/api/content/photo-page-content', { skipAuth: true });
  }

  async updatePhotoPageContent(data) {
    return this.request('/api/content/photo-page-content', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Film Landing Content
  async getFilmLandingContent() {
    return this.request('/api/content/film-landing', { skipAuth: true });
  }

  async updateFilmLandingContent(data) {
    return this.request('/api/content/film-landing', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }


  // Settings
  async getSettings() {
    return this.request('/api/content/settings', { skipAuth: true });
  }

  async updateSettings(data) {
    return this.request('/api/content/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Generic methods for flexibility
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  async upload(endpoint, formData, options = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
      ...options,
    });

    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }
}

export const api = new ApiService();
